import XLSX from "xlsx";

export const typeGroupNames = [
  "BARTMAKETAXONOMY",
  "BARTMODELTAXONOMY",
  "BARTTRIMTAXONOMY",
  "BARTCREATIVEAGENCYTAXONOMY",
  "BARTSTRATEGYTAXONOMY",
  "BARTRETAILOFFERTYPETAXONOMY",
  "BARTCREATIVEUNITTAXONOMY",
  "BARTFORMATTAXONOMY",
  "BARTCOUNTRYTAXONOMY",
  "BARTLANGUAGETAXONOMY",
  "BARTCREATIVEVERSIONTAXONOMY",
  "BARTDIMENSIONTAXONOMY",
  "BARTCREATIVEDISPLAYTAXONOMY"
];

export const getSelectValue = (data, value) => {
  let foundValue = data.find((dataObj) => dataObj.value === value);

  if (foundValue === undefined) {
    return null;
  }

  return foundValue;
};

export const searchDataByField = (
  data = [],
  primaryField = "",
  secondaryField = "",
  searchTerm = ""
) => {
  // if fields or searchTerm do not exist, return the entire data array
  if (!primaryField || !secondaryField || !searchTerm) return data;

  // if the search term includes a comma
  if (searchTerm.includes(",")) {
    // split the search term string at the comma
    const searchTerms = searchTerm.split(",");
    // make the search terms lowercase and trim whitespace, add to new array
    const searchTermsArray = [];
    searchTerms.forEach((term) => {
      searchTermsArray.push(term.trim().toLowerCase());
    });
    // remove any empty strings from search terms, add to new array
    const filteredSearchTermsArray = searchTermsArray.filter(
      (term) => term !== ""
    );

    // only return items that include all terms in the filteredSearchTermsArray
    let foundData = data.filter((item) => {
      item = item[primaryField].toLowerCase();

      const includesSearchTerms = [];
      for (let i = 0; i < filteredSearchTermsArray.length; i++) {
        includesSearchTerms.push(item.includes(filteredSearchTermsArray[i]));
      }

      // check if item includes all search terms
      if (includesSearchTerms.every((term) => term === true)) {
        return item;
      }
    });
    return foundData;
  } else {
    // if the search term does not include a comma
    let foundData = data.filter((item) => {
      searchTerm = searchTerm.trim().toLowerCase();

      // return any objects in the data array that contain the search term
      if (item[secondaryField] === searchTerm) {
        return item[secondaryField] === searchTerm;
      } else {
        return item[primaryField].toLowerCase().includes(searchTerm);
      }
    });
    return foundData;
  }
};

export const exportToExcel = (filename, data) => {
  let newData = [["Creative Names"]];
  for (let name of data) {
    newData.push([name]);
  }
  let worksheet = XLSX.utils.aoa_to_sheet(newData);
  let workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Creatives");
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Known creative type labels for filter options
export const KNOWN_CREATIVE_TYPES = ["Audio", "Display", "H5S", "Rich Media", "Static", "Tracking", "Video"];

const DCM_TYPE_LABELS = { DISPLAY: "Display", INSTREAM_VIDEO: "Video", TRACKING_TEXT: "Tracking", DISPLAY_IMAGE_GALLERY: "Image Gallery" };
const TAXONOMY_FORMAT_INDEX = 8;

// Shared creative type label resolver — used by ChangeLog, ChangeLogFilter, and Excel export
export const getCreativeTypeLabel = (dcmType, sqlType, creativeName = "") => {
  if (sqlType) {
    if (sqlType.includes("Rich Media")) return "Rich Media";
    if (sqlType.includes("Tracking")) return "Tracking";
    if (sqlType.includes("Audio")) return "Audio";
    if (sqlType.includes("Video")) return "Video";
    if (sqlType.includes("Display")) {
      if (creativeName) {
        const segments = creativeName.split("\u00BB");
        const format = segments.length > TAXONOMY_FORMAT_INDEX ? segments[TAXONOMY_FORMAT_INDEX].trim() : null;
        if (format === "H5S" || format === "Static") return format;
      }
      return "Display";
    }
    return sqlType;
  }
  if (!dcmType) return "N/A";
  // For Display types from GCM, also attempt Static/H5S taxonomy parsing
  if (dcmType === "DISPLAY" && creativeName) {
    const segments = creativeName.split("\u00BB");
    const format = segments.length > TAXONOMY_FORMAT_INDEX ? segments[TAXONOMY_FORMAT_INDEX].trim() : null;
    if (format === "H5S" || format === "Static") return format;
  }
  if (DCM_TYPE_LABELS[dcmType]) return DCM_TYPE_LABELS[dcmType];
  return dcmType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\B\w+/g, (w) => w.toLowerCase());
};

// Export change log table to Excel, excluding the Preview column
export const exportChangeLogToExcel = (filename, changeLogData = [], profileEmailMap = {}, dcmCreativesMap = {}, sqlCreativeTypeMap = {}, advertiserMap = null, sqlAdvertiserIdMap = null) => {
  // Header row (exclude Preview)
  const header = ["Email", "ID", "Creative Name", "Creative Type"];
  if (advertiserMap) header.push("Advertiser");
  header.push("Timestamp");
  const sheetData = [header];

  for (const row of changeLogData) {
    const profileName = profileEmailMap[row.userProfileId] || row.userProfileName || "";
    const id = row.objectId || "";
    // Prefer GCM name, fallback to changelog name (slice off first 12 chars)
    let creativeName = dcmCreativesMap[row.objectId]?.name || "";
    if (!creativeName && row.newValue) {
      creativeName = String(row.newValue);
      if (creativeName.length > 12) creativeName = creativeName.slice(12);
    }
    const dcmType = dcmCreativesMap[row.objectId]?.type;
    const creativeType = getCreativeTypeLabel(dcmType, sqlCreativeTypeMap[row.objectId], creativeName);
    const timestamp = row.changeTime ? new Date(row.changeTime).toLocaleString() : "";

    const rowData = [profileName, id, creativeName, creativeType];
    if (advertiserMap && sqlAdvertiserIdMap) {
      rowData.push(advertiserMap[String(sqlAdvertiserIdMap[row.objectId])] || advertiserMap[String(dcmCreativesMap[row.objectId]?.advertiserId)] || "");
    }
    rowData.push(timestamp);
    sheetData.push(rowData);
  }

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "ChangeLog");
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// Search across multiple fields (any of the provided fields) for the search term.
// - `fields` is an array of field names to check on each item
// - `searchTerm` supports comma-separated terms; any term can match
//   (logical OR across terms and across fields)
export const searchTableFields = (data = [], fields = [], searchTerm = "") => {
  if (!Array.isArray(fields) || fields.length === 0 || !searchTerm) return data;

  const terms = searchTerm
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t !== "");

  if (terms.length === 0) return data;

  return data.filter((item) => {
    // Match if any field contains any of the terms
    return fields.some((field) => {
      const raw = item && item[field] !== undefined && item[field] !== null ? String(item[field]) : "";
      const value = raw.toLowerCase();
      return terms.some((term) => value.includes(term));
    });
  });
};

/**
 * Build a preview URL on-the-fly from a DCM creative object.
 * Returns { url, previewType } or null if no preview can be constructed.
 *
 * Matches by behavior pattern rather than exhaustive type strings:
 *   - Video types (INSTREAM_VIDEO, VPAID_*) → progressiveServingUrl
 *   - Rich media types (RICH_MEDIA_*) → s0.2mdn.net/{assetName}  (iframe)
 *   - Everything else with a PRIMARY asset → s0.2mdn.net/{advertiserId}/{assetName}
 */
export const buildCreativePreviewUrl = (creative) => {
  if (!creative || !creative.creativeAssets || creative.creativeAssets.length === 0) return null;

  const type = creative.type || "";

  // --- Video creatives: use progressiveServingUrl directly ---
  if (type === "INSTREAM_VIDEO" || type.startsWith("VPAID_")) {
    const videoAsset = creative.creativeAssets.find((a) => a.progressiveServingUrl);
    if (videoAsset) return { url: videoAsset.progressiveServingUrl, previewType: "video" };
    return null;
  }

  // --- Audio creatives: use progressiveServingUrl with audio player ---
  if (type === "INSTREAM_AUDIO" || type.startsWith("AUDIO_")) {
    const audioAsset = creative.creativeAssets.find((a) => a.progressiveServingUrl);
    if (audioAsset) return { url: audioAsset.progressiveServingUrl, previewType: "audio" };
    return null;
  }

  // All remaining types need advertiserId
  if (!creative.advertiserId) return null;

  const primaryAsset = creative.creativeAssets.find((a) => a.role === "PRIMARY");
  if (!primaryAsset?.assetIdentifier?.name) return null;

  const assetName = primaryAsset.assetIdentifier.name;

  // --- Rich media creatives: no advertiserId in the URL path ---
  if (type.startsWith("RICH_MEDIA_")) {
    return { url: `https://s0.2mdn.net/${assetName}`, previewType: "iframe" };
  }

  // --- All other asset-based types (DISPLAY, HTML5_BANNER, DISPLAY_IMAGE_GALLERY, etc.) ---
  const isHtml = assetName.toLowerCase().endsWith(".html") || assetName.toLowerCase().endsWith(".htm");
  const previewType = isHtml ? "iframe" : "image";
  return { url: `https://s0.2mdn.net/${creative.advertiserId}/${assetName}`, previewType };
};